import { useState, useRef, useEffect, useCallback } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Save,
  Loader2,
  CheckCircle,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ProposalCanvas,
  useProposalCanvas,
  ProposalToolbar,
  ProposalSidebar,
  VersionHistoryPanel,
  StatusBadge,
  StatusWorkflow,
  PageTabs,
} from "@/components/proposals";
import {
  getProposal,
  updateProposal,
  updateProposalStatus,
  exportProposalPptx,
} from "@/lib/api/proposals";
import { getSimulation } from "@/lib/api/simulations";
import { listAllOutputFieldGroups } from "@/lib/api/output-fields";
import { getErrorMessage } from "@/lib/api/client";
import { formatDate } from "@/lib/utils";
import type { CanvasData, ProposalStatus, OutputFieldGroupWithFields, OutputField, ProposalVersion, TemplatePage } from "@/types";

export const Route = createFileRoute('/_dashboard/proposals/$id')({
  component: ProposalEditorPage,
})

const DEFAULT_CANVAS_DATA: CanvasData = {
  version: "5.3.0",
  objects: [],
  background: "#ffffff",
};

function ProposalEditorPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const proposalId = Number(id);
  const queryClient = useQueryClient();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvas = useProposalCanvas(canvasRef);
  
  // Multi-page state
  const [pages, setPages] = useState<TemplatePage[]>([]);
  const [activePageIndex, setActivePageIndex] = useState(0);
  
  // Editor state
  const [hasSelection, setHasSelection] = useState(false);
  const [selectedElement, setSelectedElement] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState(false);
  
  // Auto-save debounce timer
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const AUTO_SAVE_DELAY = 2000; // 2 seconds

  // Fetch proposal data
  const { data: proposal, isLoading: loadingProposal } = useQuery({
    queryKey: ["proposal", proposalId],
    queryFn: () => getProposal(proposalId),
    enabled: !!proposalId,
  });

  // Fetch simulation data
  const { data: simulation } = useQuery({
    queryKey: ["simulation", proposal?.simulation_id],
    queryFn: () => getSimulation(proposal!.simulation_id),
    enabled: !!proposal?.simulation_id,
  });

  // Fetch output fields for the simulation's version
  const { data: outputFieldGroups } = useQuery({
    queryKey: ["output-field-groups", simulation?.version_id],
    queryFn: () => listAllOutputFieldGroups(simulation!.version_id),
    enabled: !!simulation?.version_id,
  });

  // Transform output groups for sidebar
  const sidebarOutputGroups = outputFieldGroups?.map((group) => ({
    name: group.name,
    fields: flattenOutputFields(group),
  })) || [];

  // Get active page
  const activePage = pages.find((p) => p.index === activePageIndex) || pages[0];

  // Initialize pages from proposal
  useEffect(() => {
    if (proposal?.current_version) {
      const version = proposal.current_version;
      
      // Support both pages_data (new) and canvas_data (legacy)
      if (version.pages_data && version.pages_data.length > 0) {
        setPages(version.pages_data);
        setActivePageIndex(version.pages_data[0]?.index ?? 0);
      } else if (version.canvas_data) {
        // Legacy: wrap single canvas_data in pages array
        const legacyPages: TemplatePage[] = [{
          index: 0,
          name: "Page 1",
          canvas_data: version.canvas_data as CanvasData,
        }];
        setPages(legacyPages);
        setActivePageIndex(0);
      } else {
        // Fallback to empty page
        const defaultPages: TemplatePage[] = [{
          index: 0,
          name: "Page 1",
          canvas_data: DEFAULT_CANVAS_DATA,
        }];
        setPages(defaultPages);
        setActivePageIndex(0);
      }
      
      setLastSavedAt(version.created_at);
    }
  }, [proposal?.current_version]);

  // Update proposal mutation
  const updateMutation = useMutation({
    mutationFn: (pagesData: TemplatePage[]) =>
      updateProposal(proposalId, {
        pages_data: pagesData,
        editable_values: {},
      }),
    onSuccess: (data) => {
      setLastSavedAt(data.current_version?.created_at || new Date().toISOString());
      setPendingChanges(false);
      queryClient.invalidateQueries({ queryKey: ["proposal", proposalId] });
      queryClient.invalidateQueries({ queryKey: ["proposal-versions", proposalId] });
    },
    onError: (error) => {
      toast.error("Erreur de sauvegarde", {
        description: getErrorMessage(error),
      });
    },
  });

  // Handle canvas changes
  const handleCanvasChange = useCallback((canvasData: CanvasData) => {
    setPages((prevPages) =>
      prevPages.map((page) =>
        page.index === activePageIndex
          ? { ...page, canvas_data: canvasData }
          : page
      )
    );
    setPendingChanges(true);

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for auto-save (only for drafts)
    saveTimeoutRef.current = setTimeout(() => {
      if (proposal?.status === "draft") {
        // Get updated pages for save
        setPages((currentPages) => {
          const updatedPages = currentPages.map((page) =>
            page.index === activePageIndex
              ? { ...page, canvas_data: canvasData }
              : page
          );
          setIsSaving(true);
          updateMutation.mutate(updatedPages, {
            onSettled: () => setIsSaving(false),
          });
          return currentPages; // Don't update state here
        });
      }
    }, AUTO_SAVE_DELAY);
  }, [activePageIndex, proposal?.status, updateMutation]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Page management handlers
  const handlePageSelect = (index: number) => {
    // Save current canvas data before switching
    const currentCanvasData = canvas.getCanvasData();
    if (currentCanvasData) {
      setPages((prevPages) =>
        prevPages.map((page) =>
          page.index === activePageIndex
            ? { ...page, canvas_data: currentCanvasData }
            : page
        )
      );
    }
    setActivePageIndex(index);
  };

  const handlePageAdd = () => {
    // Save current canvas data first
    const currentCanvasData = canvas.getCanvasData();
    if (currentCanvasData) {
      setPages((prevPages) =>
        prevPages.map((page) =>
          page.index === activePageIndex
            ? { ...page, canvas_data: currentCanvasData }
            : page
        )
      );
    }
    
    const newIndex = Math.max(...pages.map((p) => p.index)) + 1;
    const newPage: TemplatePage = {
      index: newIndex,
      name: `Page ${pages.length + 1}`,
      canvas_data: DEFAULT_CANVAS_DATA,
    };
    setPages((prevPages) => [...prevPages, newPage]);
    setActivePageIndex(newIndex);
    setPendingChanges(true);
  };

  const handlePageRemove = (index: number) => {
    if (pages.length <= 1) return;
    
    setPages((prevPages) => {
      const newPages = prevPages.filter((p) => p.index !== index);
      // If we're removing the active page, switch to another one
      if (activePageIndex === index) {
        const newActiveIndex = newPages[0]?.index ?? 0;
        setActivePageIndex(newActiveIndex);
      }
      return newPages;
    });
    setPendingChanges(true);
  };

  const handlePageRename = (index: number, name: string) => {
    setPages((prevPages) =>
      prevPages.map((page) =>
        page.index === index ? { ...page, name } : page
      )
    );
    setPendingChanges(true);
  };

  const handlePagesReorder = (newPages: TemplatePage[]) => {
    setPages(newPages);
    setPendingChanges(true);
  };

  // Manual save handler
  const handleManualSave = () => {
    // Get current canvas data
    const currentCanvasData = canvas.getCanvasData();
    const updatedPages = pages.map((page) =>
      page.index === activePageIndex && currentCanvasData
        ? { ...page, canvas_data: currentCanvasData }
        : page
    );
    
    // Reindex pages to ensure sequential indices
    const reindexedPages = updatedPages.map((page, idx) => ({
      ...page,
      index: idx,
    }));
    
    setPages(reindexedPages);
    setIsSaving(true);
    updateMutation.mutate(reindexedPages, {
      onSettled: () => setIsSaving(false),
    });
  };

  // Selection change handler
  const handleSelectionChange = (hasSelection: boolean, element: any) => {
    setHasSelection(hasSelection);
    setSelectedElement(element);
  };

  // Canvas tool handlers
  const handleAddText = () => canvas.addText();
  const handleAddShape = (type: "rect" | "circle" | "triangle" | "line") => canvas.addShape(type);
  const handleAddImage = (url: string) => canvas.addImage(url);
  const handleAddOutput = (slug: string, value: unknown, label: string) => {
    canvas.addOutputField(slug, value, label);
  };
  const handleDelete = () => canvas.deleteSelected();
  const handleSetBackground = (color: string) => canvas.setBackgroundColor(color);

  // PPTX Export handler (server-side with native elements)
  const handleExportPptx = async () => {
    // Get current page data with visual coordinates (forExport=true)
    // This ensures accurate positioning in PowerPoint
    const currentCanvasData = canvas.getCanvasData(true);
    const allPages = pages.map((page) =>
      page.index === activePageIndex && currentCanvasData
        ? { ...page, canvas_data: currentCanvasData }
        : page
    );
    
    // Sort pages by index
    const sortedPages = [...allPages].sort((a, b) => a.index - b.index);
    
    if (sortedPages.length === 0) {
      toast.error("Aucune page à exporter");
      return;
    }
    
    toast.info("Génération du PPTX en cours...");
    
    try {
      // Send structured canvas data to backend for PPTX generation
      // Elements will be converted to native PowerPoint shapes (editable)
      const pptxBlob = await exportProposalPptx(proposalId, sortedPages, 960, 540);
      
      // Download the PPTX file
      const url = window.URL.createObjectURL(pptxBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `proposition-${proposalId}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`PPTX exporté avec succès (${sortedPages.length} slide${sortedPages.length > 1 ? 's' : ''})`);
    } catch (error) {
      console.error("PPTX export error:", error);
      toast.error("Erreur lors de l'export du PPTX");
    }
  };

  // Version restored handler
  const handleVersionRestored = (version: ProposalVersion) => {
    // Support both pages_data (new) and canvas_data (legacy)
    if (version.pages_data && version.pages_data.length > 0) {
      setPages(version.pages_data);
      setActivePageIndex(version.pages_data[0]?.index ?? 0);
    } else if (version.canvas_data) {
      // Legacy: wrap single canvas_data in pages array
      const legacyPages: TemplatePage[] = [{
        index: 0,
        name: "Page 1",
        canvas_data: version.canvas_data as CanvasData,
      }];
      setPages(legacyPages);
      setActivePageIndex(0);
    }
    setLastSavedAt(version.created_at);
    setPendingChanges(false);
  };

  // Check if proposal is editable
  const isReadOnly = proposal?.status !== "draft";

  if (loadingProposal) {
    return (
      <div className="flex flex-col h-full">
        <Header />
        <div className="flex-1 p-6">
          <Skeleton className="h-[600px] w-full" />
        </div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="flex flex-col h-full">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <p>Proposition non trouvée</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <Header />

      {/* Editor Header */}
      <div className="border-b px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/proposals">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold">{proposal.name}</h1>
                <StatusBadge status={proposal.status} />
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {proposal.current_version && (
                  <Badge variant="outline">
                    v{proposal.current_version.version_number}
                  </Badge>
                )}
                {pages.length > 1 && (
                  <Badge variant="secondary">
                    {pages.length} pages
                  </Badge>
                )}
                {lastSavedAt && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Dernière sauvegarde: {formatDate(lastSavedAt)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Save status indicator */}
            {isSaving ? (
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Sauvegarde...
              </span>
            ) : pendingChanges ? (
              <span className="flex items-center gap-1 text-sm text-warning">
                <Clock className="h-4 w-4" />
                Modifications non sauvegardées
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-success">
                <CheckCircle className="h-4 w-4" />
                Sauvegardé
              </span>
            )}

            {/* Manual save button (only for drafts) */}
            {!isReadOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleManualSave}
                disabled={isSaving || !pendingChanges}
              >
                <Save className="h-4 w-4 mr-2" />
                Sauvegarder
              </Button>
            )}

            {/* Version history */}
            <VersionHistoryPanel
              proposalId={proposalId}
              currentVersionId={proposal.current_version_id}
              onVersionRestored={handleVersionRestored}
              readOnly={isReadOnly}
            />

            {/* Status workflow */}
            <StatusWorkflow
              proposalId={proposalId}
              currentStatus={proposal.status}
            />
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Page Tabs */}
          <PageTabs
            pages={pages}
            activePageIndex={activePageIndex}
            onPageSelect={handlePageSelect}
            onPageAdd={handlePageAdd}
            onPageRemove={handlePageRemove}
            onPageRename={handlePageRename}
            onPagesReorder={handlePagesReorder}
            readOnly={isReadOnly}
          />

          {/* Toolbar */}
          <ProposalToolbar
            onAddText={handleAddText}
            onAddShape={handleAddShape}
            onAddImage={handleAddImage}
            onDelete={handleDelete}
            onSetBackground={handleSetBackground}
            onExportPptx={handleExportPptx}
            hasSelection={hasSelection}
            readOnly={isReadOnly}
          />

          {/* Canvas */}
          <ScrollArea className="flex-1">
            <div className="flex items-center justify-center p-8 min-h-full">
              {activePage && (
                <ProposalCanvas
                  key={`${activePageIndex}-${simulation?.id || 'loading'}`}
                  canvasRef={canvasRef}
                  initialData={activePage.canvas_data}
                  width={960}
                  height={540}
                  readOnly={isReadOnly}
                  onChange={handleCanvasChange}
                  onSelectionChange={handleSelectionChange}
                  simulationOutputs={simulation?.outputs || {}}
                  simulationInputs={simulation?.inputs || {}}
                />
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Sidebar */}
        <ProposalSidebar
          outputGroups={sidebarOutputGroups}
          simulationOutputs={simulation?.outputs || {}}
          onAddOutput={handleAddOutput}
          selectedElement={selectedElement}
          readOnly={isReadOnly}
        />
      </div>
    </div>
  );
}

// Helper function to flatten output fields from groups
function flattenOutputFields(group: OutputFieldGroupWithFields): OutputField[] {
  const fields = [...group.fields];
  if (group.children) {
    for (const child of group.children) {
      fields.push(...flattenOutputFields(child));
    }
  }
  return fields;
}
